import type { FormattedCitation, GroupedCitations } from "../types/active-citations"
import type { Document } from "@/api/models"

import { SettingsService } from "@/services/settings-service"
import {
  findLibraryDocumentForCitation,
  formatCitationDisplay,
  getCitationSourceForLookup,
} from "@/utils/citation-utils"

export const ACTIVE_CITATIONS_LIBRARY_ITEM_CLASS_NAME =
  "border-border bg-card hover:bg-muted/30 hover:border-primary/20 focus-within:border-primary/40 focus-within:ring-ring/20 rounded-md border px-2.5 py-2 transition-[background-color,border-color,box-shadow] focus-within:ring-1"

function getDocumentSearchableText(document: Document | null): string | undefined {
  if (!document) return undefined

  const captions = Array.isArray(document.captions) ? document.captions : []
  const captionText = captions
    .flatMap((caption) => [
      caption.text,
      ...(Array.isArray(caption.highlights) ? caption.highlights : []),
    ])
    .filter(Boolean)
    .join(" ")

  return [
    document.content,
    captionText,
    document.category,
    document.sourcefile,
    document.original_sourcefile,
  ]
    .filter(Boolean)
    .join(" ")
}

export function groupCitationsByLibrary(
  citations: string[],
  librariesAndDocuments?: [string, Document][]
): GroupedCitations {
  const groupedCitationsByLibrary: GroupedCitations = new Map<string, FormattedCitation[]>()
  const settingsService = SettingsService.getInstance()
  const allowedLibraryIds = new Set(settingsService.settings.indexes.map((index) => index.id))

  citations.forEach((citation) => {
    const [libraryId] = citation.split(":")
    if (!allowedLibraryIds.has(libraryId)) {
      // Filter out citations from indexed user files
      return
    }

    const [library, fileName] = formatCitationDisplay(
      citation,
      librariesAndDocuments
    ).fileNameWithoutPage.split(": ")

    const formattedCitation: FormattedCitation = {
      fullName: citation,
      fileName,
      searchableText: getDocumentSearchableText(
        findLibraryDocumentForCitation(citation, librariesAndDocuments)
      ),
    }

    const currentLibrary = groupedCitationsByLibrary.get(library)

    if (!currentLibrary) {
      return groupedCitationsByLibrary.set(library, [formattedCitation])
    }

    const sourceKey = getCitationSourceForLookup(citation)
    const hasCurrentCitationInLibrary = !!currentLibrary.find(
      (c) => getCitationSourceForLookup(c.fullName) === sourceKey
    )

    if (hasCurrentCitationInLibrary) {
      return
    }

    currentLibrary.push(formattedCitation)
  })

  return groupedCitationsByLibrary
}

export function filterCitationsGroupedByLibrary(
  citationsGroupedByLibrary: GroupedCitations,
  normalizedQuery: string
): GroupedCitations {
  if (!normalizedQuery) return citationsGroupedByLibrary

  const filteredCitations = new Map<string, FormattedCitation[]>()

  citationsGroupedByLibrary.forEach((libraryCitations, library) => {
    const matchesLibrary = library.toLowerCase().includes(normalizedQuery)
    const matchingCitations = matchesLibrary
      ? libraryCitations
      : libraryCitations.filter((citation) => {
          const name = citation.fileName.toLowerCase()
          const full = citation.fullName.toLowerCase()
          const searchableText = citation.searchableText?.toLowerCase() ?? ""
          return (
            name.includes(normalizedQuery) ||
            full.includes(normalizedQuery) ||
            searchableText.includes(normalizedQuery)
          )
        })

    if (matchingCitations.length > 0) {
      filteredCitations.set(library, matchingCitations)
    }
  })

  return filteredCitations
}
